import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ApiService from "./ApiService";
import SearchIcon from "@mui/icons-material/Search";
import { makeStyles } from "@material-ui/core";
import MaterialTable from "./components/MaterialTable";
import TableIcons from "./screens/common/TableLogoComponent";
import InfoIcon from "@mui/icons-material/Info";
import CustomTooltip from "./components/Custom/Tooltip/Tooltip";

const useStyles = makeStyles((theme) => ({
  searchbar: {
    margin: theme.spacing(2),
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  textFieldLabelPadding: {
    "& .MuiInputLabel-outlined": {
      transform: "translate(14px, 13px) scale(1)",
      transition: "transform 150ms cubic-bezier(0, 0, 0.2, 1) 0ms",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
    "& .MuiInputBase-root:not(.Mui-focused) .MuiInputLabel-outlined": {
      transform: "translate(14px, 17px) scale(1)",
    },
    "& .MuiFormHelperText-root": {
      marginTop: theme.spacing(1), // Adjust spacing here as needed
      color: theme.palette.primary.main,
    },
  },
}));

const CardWhitelistReport = () => {
  const classes = useStyles();
  const [failed, setFailed] = useState(false);
  const [success, setSuccess] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tableData, setTableData] = useState([]);

  const [firstSixDigits, setFirstSixDigits] = useState("");
  const [lastFourDigits, setLastFourDigits] = useState("");

  useEffect(() => {
    fetchAllMerchants();
  }, []);

  useEffect(() => {
    if (success || failed) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setFailed(false);
        setResponseMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success, failed]);

  const fetchAllMerchants = () => {
    ApiService.fetchAllMerchants().then((res) => {
      setMerchants(res.data);
    });
  };

  const handleMerchantChange = (event, newValue) => {
    const selectedMer = newValue ? newValue.id : null;
    if (selectedMer) {
      setSelectedMerchant(selectedMer);
    } else {
      setSelectedMerchant(null);
      setFirstSixDigits("");
      setLastFourDigits("");
      setTableData([]);
    }
  };

  const handleFirstSixDigitsChange = (e) => {
    // Allow only numbers and limit to 6 characters
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFirstSixDigits(value);
  };

  const handleLastFourDigitsChange = (e) => {
    // Allow only numbers and limit to 4 characters
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setLastFourDigits(value);
  };

  const handleClose = () => {
    setLoading(false);
  };
  const handleSearch = () => {
    if (!selectedMerchant) {
      setResponseMessage("Please select a Merchant");
      setFailed(true);
      return;
    }

    if (firstSixDigits.length !== 6) {
      setResponseMessage("First six Digits Field must be 6 digits.");
      setFailed(true);
      return;
    }

    if (lastFourDigits.length !== 4) {
      setResponseMessage("Last four digits Field must be 4 digits.");
      setFailed(true);
      return;
    }
    setLoading(true);
    ApiService.checkMerchantCardWhiteList(
      selectedMerchant,
      firstSixDigits,
      lastFourDigits
    )
      .then((res) => {
        if (res.data.status === "OK") {
          setLoading(false);
          setTableData(res.data.responseObject);
          setSuccess(true);
          setFailed(false);
          setResponseMessage(res.data.responseMessage);
        } else {
          setLoading(false);
          setTableData([]);
          setSuccess(false);
          setFailed(true);
          setResponseMessage(res.data.responseMessage);
        }
      })
      .catch((e) => {
        setLoading(false);
        setTableData([]);
        setSuccess(false);
        setFailed(true);
        setResponseMessage("Error in Fetching Data");
      });
  };
  return (
    <>
      <Paper style={{ margin: "1em", padding: "1em" }}>
        <Typography component="h2" variant="h6" color="primary" gutterBottom>
          Card Whitelist Report
        </Typography>
        {failed && (
          <Alert severity="error" elevation="0">
            {" "}
            {responseMessage}
          </Alert>
        )}
        {success && (
          <Alert severity="success" elevation="0">
            {" "}
            {responseMessage}
          </Alert>
        )}

        <Divider light className={classes.searchbar} />

        <Box>
          <Grid
            container
            style={{ marginTop: 20 }}
            spacing={2}
            justifyContent="space-evenly"
          >
            {" "}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Autocomplete
                options={merchants}
                getOptionLabel={(option) =>
                  option ? option.merchantName + "-" + option.code : ""
                }
                value={
                  merchants.find(
                    (merchant) => merchant.id === selectedMerchant
                  ) || null
                }
                style={{ marginTop: "0px", width: "100%" }}
                onChange={handleMerchantChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Merchants"
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2.8}>
              <TextField
                id="first_six_digits"
                label="First Six Digits"
                variant="outlined"
                helperText="Enter First Six Digits of Card"
                className={classes.textFieldLabelPadding}
                sx={{
                  width: "100%",
                  marginTop: "0px",
                  "& .MuiInputBase-root": {
                    height: 38,
                  },
                }}
                value={firstSixDigits}
                onChange={handleFirstSixDigitsChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2.8}>
              <TextField
                id="last_four_digits"
                label="Last Four Digits"
                variant="outlined"
                helperText="Enter Last Four Digits of Card"
                className={classes.textFieldLabelPadding}
                sx={{
                  width: "100%",
                  marginTop: "0px",
                  "& .MuiInputBase-root": {
                    height: 38,
                  },
                }}
                value={lastFourDigits}
                onChange={handleLastFourDigitsChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{
                  marginTop: "0px",
                  width: "100%",
                }}
                className={classes.button}
                startIcon={<SearchIcon style={{ fontSize: "1.2em" }} />}
                fullWidth
                sx={{
                  "&.MuiButton-root": {
                    height: 37,
                  },
                }}
                onClick={handleSearch}
              >
                Search
              </Button>
              <Backdrop
                open={loading}
                className={classes.backdrop}
                onClick={handleClose}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
            </Grid>
          </Grid>

          <MaterialTable
            style={{ margin: "2em 0 0 0" }}
            title=""
            icons={TableIcons.icons}
            columns={[
              { field: "id", title: "ID" },
              {
                field: "merchantId",
                title: "Merchant ID",
                render: (rowData) => (
                  <CustomTooltip title={rowData.merchantId} copy={true}>
                    <InfoIcon />
                  </CustomTooltip>
                ),
              },
              {
                field: "resellerId",
                title: "Reseller ID",
                render: (rowData) => (
                  <CustomTooltip title={rowData.resellerId} copy={true}>
                    <InfoIcon />
                  </CustomTooltip>
                ),
              },

              { field: "approvalCode", title: "Approval Code" },
            ]}
            data={tableData}
            options={{
              search: false,
              exportButton: true,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
              padding: "default",
              showEmptyDataSourceMessage: true,
              selection: false,
              actionsColumnIndex: -1,
              tableLayout: "auto",
              emptyRowsWhenPaging: false,
            }}
          />
        </Box>
      </Paper>
    </>
  );
};

export default CardWhitelistReport;
